import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2, Award } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";

const empty = {
    name: "",
    issuer: "",
    year: "",
    credential_id: "",
    credential_url: "",
    badge_url: "",
    description: "",
    order: 0,
};

export default function AdminCertifications() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const res = await api.get("/certifications");
        setItems(res.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this certification?")) return;
        await api.delete(`/admin/certifications/${id}`);
        toast.success("Certification deleted");
        load();
    };

    return (
        <div data-testid="admin-certifications-page">
            <Header
                title="Certifications"
                subtitle="Sertifikasi, lisensi, dan program training yang Anda dimiliki — tampil sebagai card di halaman publik."
                action={
                    <button onClick={() => setEditing({ ...empty })} data-testid="cert-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover">
                        <Plus size={16} /> New certification
                    </button>
                }
            />

            {loading ? (
                <div className="mono-label">Loading…</div>
            ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                    {items.map((c) => (
                        <div key={c.id} className="p-5 rounded-xl border border-white/5 bg-ink-800/40 flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {c.badge_url ? <img src={c.badge_url} alt={c.issuer} className="w-full h-full object-contain p-1.5" /> : <Award size={20} className="text-black" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-white font-medium truncate">{c.name}</div>
                                <div className="text-xs text-ink-200 truncate">{c.issuer}</div>
                                <div className="mt-1 mono-label">{c.year}</div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => setEditing({ ...c })} data-testid={`cert-edit-${c.id}`} className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white"><Pencil size={14} /></button>
                                <button onClick={() => remove(c.id)} data-testid={`cert-delete-${c.id}`} className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && (
                        <div className="col-span-full p-12 text-center text-ink-300 border border-dashed border-white/10 rounded-xl">No certifications yet.</div>
                    )}
                </div>
            )}

            {editing && <CertEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function CertEditor({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const update = (k) => (e) => {
        const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.put(`/admin/certifications/${initial.id}`, data);
            else await api.post("/admin/certifications", data);
            toast.success(isEdit ? "Certification updated" : "Certification created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="cert-editor">
            <div className="w-full max-w-xl bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit certification" : "New certification"}</div>
                        <h2 className="font-display text-xl text-white">{data.name || "Untitled"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="cert-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200"><X size={16} /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <Card title="Certification details">
                        <Field label="Name (e.g. 'Junior Web Developer')"><input className="ainp" value={data.name} onChange={update("name")} /></Field>
                        <Field label="Issuer (e.g. 'BNSP', 'AWS')"><input className="ainp" value={data.issuer} onChange={update("issuer")} /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Year / Period"><input className="ainp" value={data.year} onChange={update("year")} placeholder="2022 or 2024 – Present" /></Field>
                            <Field label="Credential ID (optional)"><input className="ainp" value={data.credential_id} onChange={update("credential_id")} /></Field>
                        </div>
                        <Field label="Credential URL (optional)"><input className="ainp" value={data.credential_url} onChange={update("credential_url")} placeholder="https://verify.example.com/xxx" /></Field>
                        <Field label="Badge / logo URL (tip: pakai logo.clearbit.com/{domain})">
                            <input className="ainp" value={data.badge_url} onChange={update("badge_url")} placeholder="https://logo.clearbit.com/bnsp.go.id" />
                        </Field>
                        <Field label="Description"><textarea rows={3} className="ainp" value={data.description} onChange={update("description")} /></Field>
                        <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={update("order")} /></Field>
                    </Card>
                </div>
            </div>
        </div>
    );
}
